/**
 * Token breakdown and cost calculation functions for Anthropic usage data
 */

import type {
  AnthropicUsageData,
  AnthropicTokenBreakdown,
  AnthropicCostBreakdown,
  AnthropicModelPricing
} from './types';
import {
  ANTHROPIC_FAST_MODE_PRICING,
  ANTHROPIC_MODEL_PRICING,
  ANTHROPIC_WEB_SEARCH_COST_PER_REQUEST
} from './pricing';

/** Set of models already warned about, to avoid log spam */
const warnedModels = new Set<string>();
const warnedFastModeModels = new Set<string>();

/**
 * 料金テーブル参照。モデルIDの日付サフィックス有無の揺れを吸収する:
 * 完全一致 → 日付サフィックス除去で一致 → テーブル側の日付付きキーと前方一致
 */
function lookupPricingTable(
  table: Record<string, AnthropicModelPricing>,
  model: string
): AnthropicModelPricing | undefined {
  if (table[model]) return table[model];
  const dateless = model.replace(/-\d{8}$/, '');
  if (dateless !== model && table[dateless]) return table[dateless];
  const datedKey = Object.keys(table).find(key => key.replace(/-\d{8}$/, '') === dateless);
  return datedKey ? table[datedKey] : undefined;
}

function resolveAnthropicPricing(usage: AnthropicUsageData): AnthropicModelPricing | undefined {
  const model = usage.model;
  if (!model) {
    return undefined;
  }

  if (usage.speed === 'fast') {
    const fastPricing = lookupPricingTable(ANTHROPIC_FAST_MODE_PRICING, model);
    if (fastPricing) {
      return fastPricing;
    }
    if (!warnedFastModeModels.has(model)) {
      warnedFastModeModels.add(model);
      console.warn(`[Anthropic Pricing] Fast mode pricing is not configured for "${model}" - falling back to standard pricing.`);
    }
  }

  return lookupPricingTable(ANTHROPIC_MODEL_PRICING, model);
}

/**
 * Calculate token breakdown from a usage record.
 */
export function getAnthropicTokenBreakdown(usage: AnthropicUsageData): AnthropicTokenBreakdown {
  const baseInput = usage.uncached_input_tokens ?? 0;
  const cacheRead = usage.cache_read_input_tokens ?? 0;
  const cacheWrite5m = usage.cache_creation?.ephemeral_5m_input_tokens ?? 0;
  const cacheWrite1h = usage.cache_creation?.ephemeral_1h_input_tokens ?? 0;
  const cacheWriteOther = usage.cache_creation?.input_tokens ?? 0;
  const serverToolInput = usage.server_tool_use?.input_tokens ?? 0;
  const outputTokens = usage.output_tokens ?? 0;
  const totalInputTokens = baseInput + cacheRead + cacheWrite5m + cacheWrite1h + cacheWriteOther + serverToolInput;
  const totalTokens = totalInputTokens + outputTokens;
  const webSearchRequests = usage.server_tool_use?.web_search_requests
    ?? usage.web_search_uses
    ?? 0;

  return {
    baseInput,
    cacheRead,
    cacheWrite5m,
    cacheWrite1h,
    cacheWriteOther,
    serverToolInput,
    outputTokens,
    totalInputTokens,
    totalTokens,
    webSearchRequests
  };
}

/**
 * Calculate cost from usage data
 */
export function calculateAnthropicCostBreakdown(usage: AnthropicUsageData): AnthropicCostBreakdown {
  if (!usage.model) {
    return {
      baseInputCost: 0,
      cacheReadCost: 0,
      cacheWrite5mCost: 0,
      cacheWrite1hCost: 0,
      cacheWriteOtherCost: 0,
      serverToolCost: 0,
      outputCost: 0,
      webSearchCost: 0,
      totalCost: 0
    };
  }

  const pricing = resolveAnthropicPricing(usage);
  if (!pricing) {
    if (!warnedModels.has(usage.model)) {
      warnedModels.add(usage.model);
      console.warn(`[Anthropic Pricing] Unknown model "${usage.model}" - cost will be zero. Consider adding to pricing table.`);
    }
    return {
      baseInputCost: 0,
      cacheReadCost: 0,
      cacheWrite5mCost: 0,
      cacheWrite1hCost: 0,
      cacheWriteOtherCost: 0,
      serverToolCost: 0,
      outputCost: 0,
      webSearchCost: 0,
      totalCost: 0
    };
  }

  const {
    baseInput,
    cacheRead,
    cacheWrite5m,
    cacheWrite1h,
    cacheWriteOther,
    serverToolInput,
    outputTokens,
    webSearchRequests
  } = getAnthropicTokenBreakdown(usage);

  // Batch API (service_tier === 'batch') はトークン課金が 50% 割引。
  // priority はトークン単価が標準と同一（コミット容量課金）なので 1.0。
  // flex / flex_discount は公開単価が無いため標準扱い（cost_report 補正で吸収）。
  // web 検索などツール課金には適用しない
  const batchMultiplier = usage.service_tier === 'batch' ? 0.5 : 1.0;
  // inference_geo "us" は全トークン区分に 1.1x（Opus 4.6 / Sonnet 4.6 以降のデータレジデンシー課金）。
  // 現状 group_by に inference_geo を含めていない（最大5次元の制限）ため通常は null だが、
  // 含めた場合に正しく計算できるよう乗数は維持する
  const geoMultiplier = usage.inference_geo === 'us' ? 1.1 : 1.0;
  const tierMultiplier = batchMultiplier * geoMultiplier;

  const baseInputCost = baseInput > 0 ? baseInput * pricing.baseInput * tierMultiplier : 0;
  const cacheReadCost = cacheRead > 0 ? cacheRead * (pricing.cacheRead ?? pricing.baseInput) * tierMultiplier : 0;
  const cacheWrite5mCost = cacheWrite5m > 0 ? cacheWrite5m * (pricing.cacheWrite5m ?? pricing.baseInput) * tierMultiplier : 0;
  const cacheWrite1hCost = cacheWrite1h > 0 ? cacheWrite1h * (pricing.cacheWrite1h ?? pricing.baseInput) * tierMultiplier : 0;
  const cacheWriteOtherCost = cacheWriteOther > 0 ? cacheWriteOther * (pricing.cacheWriteDefault ?? pricing.baseInput) * tierMultiplier : 0;
  const serverToolCost = serverToolInput > 0 ? serverToolInput * (pricing.serverToolInput ?? pricing.baseInput) * tierMultiplier : 0;
  const outputCost = outputTokens > 0 ? outputTokens * pricing.output * tierMultiplier : 0;
  const webSearchCost = webSearchRequests > 0
    ? webSearchRequests * (pricing.webSearch ?? ANTHROPIC_WEB_SEARCH_COST_PER_REQUEST)
    : 0;

  const totalCost = baseInputCost + cacheReadCost + cacheWrite5mCost + cacheWrite1hCost +
    cacheWriteOtherCost + serverToolCost + outputCost + webSearchCost;

  return {
    baseInputCost,
    cacheReadCost,
    cacheWrite5mCost,
    cacheWrite1hCost,
    cacheWriteOtherCost,
    serverToolCost,
    outputCost,
    webSearchCost,
    totalCost
  };
}

export function calculateAnthropicCost(usage: AnthropicUsageData): number {
  return calculateAnthropicCostBreakdown(usage).totalCost;
}
